import { useEffect } from 'react'
import type { BookingResponse } from '@dialogue/timekeeper'
import { useDispatch, useSelector } from 'APP/Hooks/redux'
import { providerBookingActions } from 'APP/Store/ProviderBooking'
import {
  selectBooking,
  selectBookingLoading,
  selectBookingError,
} from 'APP/Store/ProviderBooking/selectors'

export type UseGetBookingResult = {
  booking: BookingResponse | null
  loading: boolean
  error: Error | null
  isReady: boolean
}

/**
 * Hook for fetching booking data by booking ID
 * @param {string} bookingId - The booking ID to fetch
 * @returns object with booking data, loading state, error, and a ready flag
 *   (`isReady` is false while a booking is expected but not yet loaded)
 */
export const useGetBooking = (bookingId: string | undefined): UseGetBookingResult => {
  const dispatch = useDispatch()
  const booking = useSelector(selectBooking)
  const loading = useSelector(selectBookingLoading)
  const error = useSelector(selectBookingError)

  useEffect(() => {
    if (bookingId) {
      dispatch(providerBookingActions.getBooking(bookingId))
    }
  }, [bookingId, dispatch])

  // The store is in-memory, so the booking can go missing while this component
  // stays mounted (e.g. a cold-start remount). Re-fetch whenever a booking is
  // expected but absent.
  useEffect(() => {
    if (bookingId && !booking && !loading && !error) {
      dispatch(providerBookingActions.getBooking(bookingId))
    }
  }, [bookingId, booking, loading, error, dispatch])

  const isReady = !bookingId || booking != null

  return {
    booking,
    loading,
    error,
    isReady,
  }
}
